(() => {
  'use strict';
  const model = window.KillinBinderModel, store = window.KillinBinderStore;
  const catalog = window.KillinBinderCatalog, shopping = window.KillinBinderShopping;
  if (!model || !store || !catalog || !shopping) return;
  const instances = new WeakMap();
  function element(tag, text, className) {
    const node = document.createElement(tag);
    if (text !== undefined && text !== null) node.textContent = text;
    if (className) node.className = className;
    return node;
  }
  function button(text, action, className = 'button button-outline') {
    const node = element('button', text, className);
    node.type = 'button'; node.addEventListener('click', action);
    return node;
  }
  function select(label, choices, selected = '') {
    const wrapper = element('label', label), control = element('select');
    control.setAttribute('aria-label', label);
    for (const [value, text] of choices) {
      const option = element('option', text); option.value = value; control.append(option);
    }
    control.value = String(selected); wrapper.append(control);
    return {wrapper, control};
  }
  function input(label, value = '', type = 'text') {
    const wrapper = element('label', label), control = element('input');
    control.type = type; control.value = value; control.setAttribute('aria-label', label); wrapper.append(control);
    return {wrapper, control};
  }
  function money(value, currency = 'CAD') {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency}).format(value);
  }
  function qr(target, text) {
    const code = window.qrcodegen.QrCode.encodeText(text, window.qrcodegen.QrCode.Ecc.MEDIUM);
    const namespace = 'http://www.w3.org/2000/svg', svg = document.createElementNS(namespace, 'svg');
    svg.setAttribute('viewBox', `0 0 ${code.size + 8} ${code.size + 8}`);
    svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', 'Scan to transfer your binder to your phone');
    const background = document.createElementNS(namespace, 'rect');
    background.setAttribute('width', code.size + 8); background.setAttribute('height', code.size + 8); background.setAttribute('fill', '#fff');
    const path = document.createElementNS(namespace, 'path'); let drawing = '';
    for (let y = 0; y < code.size; y++) for (let x = 0; x < code.size; x++) if (code.getModule(x, y)) drawing += `M${x + 4} ${y + 4}h1v1h-1z`;
    path.setAttribute('d', drawing); path.setAttribute('fill', '#111'); svg.append(background, path); target.replaceChildren(svg);
  }
  function init(root) {
    if (instances.has(root)) return instances.get(root);
    const panel = element('div', null, 'binder-builder'); panel.hidden = true; root.append(panel);
    const toolbar = element('div', null, 'bb-toolbar'), accountBar = element('div', null, 'bb-account');
    const status = element('p', '', 'bb-status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    const paging = element('div', null, 'bb-paging'), grid = element('div', null, 'bb-spread');
    const bookFrame = element('div', null, 'bb-book bv-page-turner');
    const previousPage = button('‹', () => { page--; draw(); }, 'bv-page-turn');
    const nextPage = button('›', () => { page++; draw(); }, 'bv-page-turn bv-page-turn-next');
    previousPage.setAttribute('aria-label', 'Previous binder page'); nextPage.setAttribute('aria-label', 'Next binder page');
    bookFrame.append(previousPage, grid, nextPage);
    const empty = element('div', null, 'bb-empty'), conflictBox = element('div', null, 'bb-conflict'); conflictBox.hidden = true;
    const pickerHost = element('div', null, 'bb-modal-host'); pickerHost.hidden = true;
    panel.append(toolbar, accountBar, status, conflictBox, paging, empty, bookFrame, pickerHost);
    let manifest, active = false, bookKey = '', page = 0, selectedSlot = '', renderVersion = 0;
    let lastState, modalVersion = 0, modalReturnFocus, cycleBusy = false, lastCycle = 0, lastSaveStatus = '';
    const undoStack = [], savedHidden = new Map();
    const mobile = matchMedia('(max-width:700px), (max-width:1100px) and (orientation:portrait)');
    const pageSize = () => mobile.matches ? 9 : 18;
    const book = () => store.state.binders[bookKey];
    const alert = message => { status.textContent = message; };
    const showError = error => alert(error.message || 'This action could not be completed. Please retry.');
    function edit(action) {
      const before = model.clone(store.state);
      store.change(action);
      undoStack.push({before, after: model.clone(store.state)});
      if (undoStack.length > 12) undoStack.shift();
      render();
    }
    function closeModal() {
      modalVersion++; pickerHost.hidden = true; pickerHost.replaceChildren();
      document.body.classList.remove('bb-modal-open');
      if (modalReturnFocus?.isConnected) modalReturnFocus.focus({preventScroll: true});
    }
    function modal(title) {
      closeModal(); modalReturnFocus = document.activeElement;
      const dialog = element('section', null, 'bb-modal'); dialog.setAttribute('role', 'dialog'); dialog.setAttribute('aria-modal', 'true'); dialog.setAttribute('aria-label', title);
      const heading = element('div', null, 'bb-modal-heading');
      heading.append(element('h2', title), button('Close', closeModal, 'bb-small-button'));
      const body = element('div', null, 'bb-modal-body'); dialog.append(heading, body); pickerHost.append(dialog); pickerHost.hidden = false;
      document.body.classList.add('bb-modal-open'); heading.querySelector('button').focus();
      return {body, version: modalVersion};
    }
    function confirmAction(title, description, action) {
      const {body} = modal(title);
      body.append(element('p', description), button(title, () => { action(); closeModal(); }), button('Cancel', closeModal));
    }
    function focusSlot(id) {
      selectedSlot = id;
      const target = [...grid.querySelectorAll('[data-bb-slot]')].find(node => node.dataset.bbSlot === id);
      target?.focus({preventScroll: true});
    }
    function createDialog() {
      const {body} = modal('Create a binder');
      const name = input('Binder name'), kind = select('Layout', [['pokedex', 'Pokédex · higher rarities first'], ['custom', 'Custom · blank pages']], 'pokedex');
      const generations = [...new Set(manifest.species.map(species => species.generation))];
      const range = select('Pokédex range', [['0', 'National Pokédex · all generations'], ...generations.map(id => [id, 'Generation ' + id])], '0');
      kind.control.onchange = () => { range.wrapper.hidden = kind.control.value !== 'pokedex'; };
      name.control.maxLength = 100;
      body.append(name.wrapper, kind.wrapper, range.wrapper, button('Create binder', () => {
        try { let created; edit(state => { created = model.create(state, kind.control.value, name.control.value, manifest, Number(range.control.value)); }); bookKey = created; page = 0; closeModal(); render(); }
        catch (error) { showError(error); }
      }, 'button'));
      name.control.focus();
    }
    function rename() {
      const {body} = modal('Rename binder'), name = input('Binder name', book().name);
      name.control.maxLength = 100;
      body.append(name.wrapper, button('Save name', () => { edit(state => { state.binders[bookKey].name = name.control.value; }); closeModal(); }, 'button'));
      name.control.focus();
    }
    function duplicate() {
      try {
        let target;
        edit(state => {
          target = Array.from({length: model.MAX_BINDERS}, (_, i) => 'b' + i).find(key => !state.binders[key]);
          if (!target) throw Error('You have 10 binders. Remove one before making a copy.');
          state.binders[target] = {...model.clone(state.binders[bookKey]), uid: model.id(), name: book().name + ' copy'};
        });
        bookKey = target; render();
      } catch (error) { showError(error); }
    }
    function undo() {
      const change = undoStack.pop(); if (!change) return;
      const result = model.merge(change.after, change.before, store.state);
      if (result.conflicts.length) { alert('Newer edits affect this undo. Keep the current version and edit the slot directly.'); return; }
      store.change(state => { Object.assign(state, model.normalize(result.value)); });
    }
    function rarityChoices(parent, current, action) {
      const details = element('details', null, 'bb-rarity-filter');
      details.append(element('summary', current.length ? current.join(', ') : 'All rarities'));
      const choices = element('div', null, 'bb-rarity-options');
      for (const rarity of manifest.rarities) {
        const label = element('label'), checkbox = element('input'); checkbox.type = 'checkbox'; checkbox.value = rarity; checkbox.checked = current.includes(rarity);
        checkbox.addEventListener('change', () => action([...choices.querySelectorAll('input:checked')].map(node => node.value)));
        label.append(checkbox, document.createTextNode(rarity)); choices.append(label);
      }
      details.append(choices); parent.append(details);
    }
    function toolbarRender() {
      const previousToolsOpen = toolbar.querySelector('.bb-extra-tools')?.open || false;
      const previousRarityOpen = toolbar.querySelector('.bb-rarity-filter')?.open || false;
      toolbar.replaceChildren(); accountBar.replaceChildren();
      const extras = element('details', null, 'bb-extra-tools');
      extras.open = !mobile.matches || previousToolsOpen;
      extras.append(element('summary', 'Builder tools'));
      const extraControls = element('div', null, 'bb-extra-controls'); extras.append(extraControls);
      const books = Object.entries(store.state.binders);
      if (!book() && books.length) bookKey = books[0][0];
      if (books.length) {
        const choice = select('My binders', books.map(([key, value]) => [key, value.name]), bookKey);
        choice.control.addEventListener('change', () => { bookKey = choice.control.value; page = 0; selectedSlot = ''; render(); });
        toolbar.append(choice.wrapper);
      }
      extraControls.append(button('New binder', createDialog));
      if (book()) {
        const actions = element('details', null, 'bb-actions'); actions.append(element('summary', 'Binder options'));
        actions.append(button('Rename', rename, 'bb-small-button'), button('Duplicate', duplicate, 'bb-small-button'),
          button('Delete binder', () => confirmAction('Delete binder', 'You can restore it with Undo during this session.', () => edit(state => { delete state.binders[bookKey]; })), 'bb-small-button'));
        extraControls.append(actions);
        rarityChoices(extraControls, book().rarities, values => edit(state => { state.binders[bookKey].rarities = values; }));
        extraControls.querySelector('.bb-rarity-filter').open = previousRarityOpen;
        const reviewButton = button('Add in stock cards to cart', () => reviewDialog().catch(showError), 'button');
        toolbar.append(reviewButton);
        extraControls.append(button('Transfer to phone', () => transferDialog().catch(showError)));
      }
      const undoButton = button('Undo', undo, 'bb-small-button'); undoButton.disabled = !undoStack.length; extraControls.append(undoButton);
      toolbar.append(extras);
      if (document.body.hasAttribute('data-kiosk')) accountBar.append(element('span', 'Transfer your draft to your phone to save it.'));
      else if (store.owner === 'guest') {
        const save = button('Save to my account', () => store.login().catch(showError), 'bb-small-button');
        accountBar.append(save, element('span', store.configured ? 'Sign in to open your binders on another device.' : 'Account saving is being connected. Drafts save on this device.'));
      } else {
        accountBar.append(button('Sync now', () => store.sync().catch(showError), 'bb-small-button'), button('Sign out', () => store.logout().catch(showError), 'bb-small-button'));
        const guest = store.guestDraft();
        if (Object.values(guest.binders).some(draft => !Object.values(store.state.binders).some(saved => saved.uid === draft.uid))) accountBar.append(button('Import this device’s drafts', () => { try { store.importGuest(); } catch (error) { showError(error); } }, 'bb-small-button'));
      }
    }
    function visibleSlots() {
      const binder = book(); if (!binder) return [];
      const start = page * pageSize();
      return binder.order.slice(start, start + pageSize());
    }
    function pageRender() {
      const binder = book(); paging.replaceChildren();
      if (!binder) return;
      page = Math.min(page, Math.max(0, Math.ceil(binder.order.length / pageSize()) - 1));
      previousPage.disabled = page === 0;
      nextPage.disabled = (page + 1) * pageSize() >= binder.order.length;
      const first = page * (pageSize() / 9) + 1, last = Math.min(first + pageSize() / 9 - 1, Math.ceil(binder.order.length / 9));
      paging.append(element('span', (first === last ? 'Page ' + first : 'Pages ' + first + '–' + last) + ' of ' + Math.ceil(binder.order.length / 9)));
      if (binder.kind === 'pokedex') {
        const included = new Set(binder.order.map(id => binder.slots[id].species));
        const speciesList = manifest.species.filter(species => included.has(species.id));
        const current = binder.slots[binder.order[page * pageSize()]]?.species;
        const generation = speciesList.find(species => species.id === current)?.generation || 1;
        const generations = [...new Set(speciesList.map(species => species.generation))];
        const gen = select('Generation', generations.map(id => [id, 'Generation ' + id]), generation);
        gen.control.onchange = () => jumpSpecies(speciesList.find(species => species.generation === Number(gen.control.value)).id);
        const jump = select('Jump to Pokémon', speciesList.map(species => [species.id, '#' + species.id + ' ' + species.name]), current);
        jump.control.onchange = () => jumpSpecies(Number(jump.control.value));
        if (generations.length > 1) paging.append(gen.wrapper);
        paging.append(jump.wrapper);
      } else {
        paging.append(button('Add page', () => { try { edit(state => model.addPage(state.binders[bookKey])); page = Math.floor((book().order.length - 1) / pageSize()); draw(); } catch (error) { showError(error); } }, 'bb-small-button'));
        if (binder.order.length > 9) paging.append(button('Remove page', () => confirmAction('Remove page', 'Remove the last nine slots on this spread. Undo can restore them.', () => {
          edit(state => {
            const value = state.binders[bookKey], start = Math.min((page + 1) * pageSize(), value.order.length) - 9;
            const removed = value.order.splice(start, 9); removed.forEach(id => { delete value.slots[id]; });
          });
        }), 'bb-small-button'));
        if (selectedSlot) {
          paging.append(button('Move slot left', () => moveSlot(-1), 'bb-small-button'), button('Move slot right', () => moveSlot(1), 'bb-small-button'));
        }
      }
    }
    function jumpSpecies(id) {
      const index = book().order.findIndex(slot => book().slots[slot].species === id);
      if (index >= 0) { page = Math.floor(index / pageSize()); draw(); }
    }
    function moveSlot(direction) {
      const index = book().order.indexOf(selectedSlot), target = index + direction;
      if (target < 0 || target >= book().order.length) return;
      edit(state => { const order = state.binders[bookKey].order; [order[index], order[target]] = [order[target], order[index]]; });
      page = Math.floor(target / pageSize()); draw(); focusSlot(selectedSlot);
    }
    function pick(slotId, card, filters) {
      edit(state => {
        const slot = state.binders[bookKey].slots[slotId]; slot.choice = card?.key || null;
        if (filters) Object.assign(slot, filters);
      });
      focusSlot(slotId);
    }
    async function candidates(slot) {
      const matches = model.search(slot.query || '');
      const pool = await catalog.pool(slot.species, slot.set);
      const rarities = slot.rarities === null ? book().rarities : slot.rarities;
      return pool.filter(card => (!rarities.length || rarities.includes(card.rarity)) && (!slot.finish || card.finish === slot.finish) && matches(card));
    }
    async function cycle(slotId, direction) {
      if (cycleBusy || Date.now() - lastCycle < 180) return;
      cycleBusy = true; lastCycle = Date.now(); const currentBook = bookKey;
      try {
        const slot = model.clone(book().slots[slotId]), choices = await candidates(slot);
        if (!active || bookKey !== currentBook || !model.equal(book().slots[slotId], slot)) return;
        if (!choices.length) { alert('No cards match this slot’s filters. Open the picker to change them.'); return; }
        const index = choices.findIndex(card => card.key === slot.choice);
        pick(slotId, choices[(index + direction + choices.length) % choices.length]);
      } catch (error) { showError(error); }
      finally { cycleBusy = false; }
    }
    function slotElement(slotId) {
      const slot = book().slots[slotId], card = catalog.get(slot.choice);
      const species = manifest.species.find(value => value.id === slot.species);
      const pocket = element('article', null, 'bb-slot'); pocket.tabIndex = 0; pocket.dataset.bbSlot = slotId;
      pocket.setAttribute('aria-label', species ? species.name + ' binder slot' : 'Custom binder slot');
      pocket.addEventListener('focusin', () => { selectedSlot = slotId; });
      const heading = element('div', null, 'bb-slot-heading');
      if (species) heading.append(element('strong', species.name), element('span', '#' + species.id));
      else heading.append(element('strong', card?.name || 'Choose a card'));
      const art = button('', () => picker(slotId).catch(showError), 'bb-card');
      art.setAttribute('aria-label', 'Choose card for ' + (species?.name || card?.name || 'this slot'));
      if (card?.image) { const image = element('img'); image.src = card.image; image.alt = card.name; image.loading = 'lazy'; art.append(image); }
      else art.append(element('span', slot.choice ? 'Loading card…' : '+'));
      const artFrame = element('div', null, 'bb-art'); artFrame.append(art);
      if (card && window.KillinWishlist && !document.body.hasAttribute('data-kiosk')) {
        const saved = window.KillinWishlist.has(card.key);
        const wish = button(saved ? '♥' : '♡', () => {
          if (window.KillinWishlist.toggle({key: card.key, type: 'printing', title: card.name, setName: card.setName, finish: card.finish})) draw();
        }, 'bb-wishlist');
        wish.setAttribute('aria-label', (saved ? 'Remove from wishlist' : 'Add to wishlist') + ' · ' + card.name + ' · ' + card.finish);
        wish.setAttribute('aria-pressed', String(saved)); artFrame.append(wish);
      }
      const stockLabel = element('span', slot.choice ? 'Checking stock…' : 'Choose a card', 'bv-stock bb-stock');
      if (slot.choice) stockLabel.dataset.bbStockKey = slot.choice;
      const details = element('div', null, 'bb-slot-details');
      details.append(element('small', card ? card.number + ' · ' + card.finish : 'Choose a printing'));
      if (card) details.title = card.name + ' · ' + card.setName + ' · ' + card.rarity;
      const controls = element('div', null, 'bb-slot-controls');
      const left = button('‹', () => cycle(slotId, -1), 'bb-cycle'), right = button('›', () => cycle(slotId, 1), 'bb-cycle');
      left.setAttribute('aria-label', 'Previous matching card'); right.setAttribute('aria-label', 'Next matching card');
      const owned = !!store.state.owned[slot.choice];
      const ownership = button(owned ? '✓ Owned' : 'Mark owned', () => {
        if (!slot.choice) return;
        edit(state => { if (state.owned[slot.choice]) delete state.owned[slot.choice]; else state.owned[slot.choice] = true; }); focusSlot(slotId);
      }, 'bb-ownership');
      ownership.disabled = !slot.choice; ownership.setAttribute('aria-pressed', String(owned));
      ownership.setAttribute('aria-label', (owned ? 'Mark as unowned' : 'Mark as owned') + (card ? ' · ' + card.name + ' · ' + card.finish : ''));
      controls.append(left, ownership, right); pocket.append(heading, artFrame, stockLabel, details, controls);
      pocket.addEventListener('wheel', event => {
        if (selectedSlot !== slotId || !pocket.contains(document.activeElement) || event.ctrlKey || Math.abs(event.deltaY) < 8) return;
        event.preventDefault(); cycle(slotId, event.deltaY > 0 ? 1 : -1);
      }, {passive: false});
      pocket.addEventListener('keydown', event => {
        if (event.target.matches('input,select,textarea')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); event.stopPropagation(); cycle(slotId, event.key === 'ArrowRight' ? 1 : -1); }
      });
      return pocket;
    }
    function draw() {
      const ticket = ++renderVersion; grid.replaceChildren(); pageRender();
      if (!book()) return;
      const slots = visibleSlots();
      for (let offset = 0; offset < slots.length; offset += 9) {
        const sheet = element('div', null, 'bb-sheet');
        slots.slice(offset, offset + 9).forEach(id => sheet.append(slotElement(id)));
        while (sheet.children.length < 9) sheet.append(element('div', null, 'bb-slot bb-blank'));
        grid.append(sheet);
      }
      const missing = slots.map(id => book().slots[id].choice).filter(key => key && !catalog.get(key));
      if (missing.length) catalog.resolve(missing).then(() => { if (active && ticket === renderVersion) draw(); }).catch(showError);
      const selected = slots.map(id => book().slots[id].choice).filter(Boolean);
      shopping.availability(selected).then(rows => {
        if (!active || ticket !== renderVersion) return;
        const stock = new Map(rows.map(row => [row.key, row]));
        for (const label of grid.querySelectorAll('[data-bb-stock-key]')) {
          const row = stock.get(label.dataset.bbStockKey), available = !!row?.id && !row.reason;
          label.textContent = available ? 'In stock' : 'Out of stock';
          label.dataset.stock = available ? 'available' : 'unavailable';
          if (available) label.title = row.condition + ' · ' + money(row.price, row.currency);
        }
      }).catch(() => {
        if (active && ticket === renderVersion) for (const label of grid.querySelectorAll('[data-bb-stock-key]')) {
          label.textContent = 'Check stock'; label.title = 'Stock could not be checked. Try Add in stock cards to cart.';
        }
      });
    }
    function render() {
      if (!active || !manifest) return;
      lastState = store.state; toolbarRender();
      empty.hidden = !!book(); empty.replaceChildren(); bookFrame.hidden = paging.hidden = !book();
      if (!book()) empty.append(element('h2', 'Build a binder of your favourites'), element('p', 'Choose a Pokédex layout or fill your own nine-pocket pages.'), button('Create your first binder', createDialog, 'button'));
      draw(); updateStatus();
    }
    function updateStatus() {
      if (lastSaveStatus !== store.status) { alert(store.status); lastSaveStatus = store.status; }
      conflictBox.hidden = !store.conflict; conflictBox.replaceChildren();
      if (store.conflict) conflictBox.append(element('p', 'A selection changed on another device.'), button('Keep my edits', () => store.resolveConflict(true)), button('Use saved edits', () => store.resolveConflict(false)));
    }
    async function picker(slotId) {
      const slot = model.clone(book().slots[slotId]), currentBook = bookKey;
      const species = manifest.species.find(value => value.id === slot.species);
      const view = modal(species ? 'Choose ' + species.name : 'Choose a card'), filters = element('form', null, 'bb-picker-filters');
      const query = input('Search cards', slot.query || '', 'search'); query.control.maxLength = 400;
      const set = select('Set', [['', 'All sets'], ...manifest.sets.map(value => [value.id, value.name])], slot.set || '');
      const rarityValue = slot.rarities === null ? 'inherit' : slot.rarities[0] || '';
      const rarity = select('Rarity', [['inherit', 'Use binder rarity filter'], ['', 'All rarities'], ...manifest.rarities.map(value => [value, value])], rarityValue);
      const finish = select('Finish', [['', 'All finishes'], ...manifest.finishes.map(value => [value, value])], slot.finish || '');
      const submit = element('button', 'Find cards', 'button'); submit.type = 'submit';
      filters.append(query.wrapper, set.wrapper, rarity.wrapper, finish.wrapper, submit);
      const message = element('p', '', 'bb-status'), results = element('div', null, 'bb-picker-results'); message.setAttribute('role', 'status');
      const more = button('Show more cards', () => { shown += 60; paint(); }); more.hidden = true;
      view.body.append(filters, message, results, more, button('Clear this slot', () => { pick(slotId, null); closeModal(); }));
      let found = [], shown = 60, requestId = 0;
      function chosenFilters() { return {query: query.control.value, set: Number(set.control.value) || 0, finish: finish.control.value, rarities: rarity.control.value === 'inherit' ? null : rarity.control.value ? [rarity.control.value] : []}; }
      function paint() {
        results.replaceChildren();
        for (const card of found.slice(0, shown)) {
          const choose = button('', () => { if (bookKey === currentBook) pick(slotId, card, chosenFilters()); closeModal(); focusSlot(slotId); }, 'bb-thumbnail');
          choose.setAttribute('aria-label', card.name + ' · ' + card.setName + ' · ' + card.number + ' · ' + card.finish);
          if (card.image) { const image = element('img'); image.src = card.image; image.alt = card.name; image.loading = 'lazy'; choose.append(image); }
          choose.append(element('span', card.number + ' · ' + card.finish), element('small', card.setName));
          if (store.state.owned[card.key]) choose.append(element('small', '✓ Owned'));
          results.append(choose);
        }
        message.textContent = found.length + ' matching printings'; more.hidden = shown >= found.length;
      }
      async function find() {
        const ticket = ++requestId; message.textContent = 'Loading matching cards…';
        try {
          const chosen = chosenFilters(), matches = model.search(chosen.query);
          const pool = await catalog.pool(slot.species, chosen.set, (count, total) => { if (ticket === requestId && view.version === modalVersion) message.textContent = 'Loading sets ' + count + ' / ' + total; });
          if (ticket !== requestId || view.version !== modalVersion || bookKey !== currentBook) return;
          const rarities = chosen.rarities === null ? book().rarities : chosen.rarities;
          found = pool.filter(card => matches(card) && (!rarities.length || rarities.includes(card.rarity)) && (!chosen.finish || chosen.finish === card.finish));
          found.sort((a, b) => manifest.rarities.indexOf(a.rarity) - manifest.rarities.indexOf(b.rarity) || a.name.localeCompare(b.name) || a.id - b.id || a.finish.localeCompare(b.finish));
          shown = 60; paint();
        } catch (error) { if (view.version === modalVersion) message.textContent = error.message; }
      }
      filters.onsubmit = event => { event.preventDefault(); find(); };
      for (const control of [set.control, rarity.control, finish.control]) control.onchange = find;
      query.control.focus(); await find();
    }
    async function reviewDialog() {
      const view = modal('Add in stock cards to cart'), currentBook = bookKey;
      const condition = select('Condition', [['ANY', 'Any condition'], ['NM', 'Near Mint'], ['LP', 'Lightly Played'], ['MP', 'Moderately Played'], ['HP', 'Heavily Played'], ['DMG', 'Damaged']], 'ANY');
      const count = input('Select first X cards', '', 'number'); count.control.min = '0'; count.control.step = '1';
      const summary = element('p', 'Checking current stock…', 'bb-status'); summary.setAttribute('role', 'status');
      const results = element('div', null, 'bb-review-list'), selected = new Set();
      const all = button('Select all available', () => { count.control.value = ''; rows.filter(row => row.id && !row.reason).forEach(row => selected.add(row.key)); paint(); });
      const add = button('Add selected to cart', submit, 'button'); add.disabled = true;
      const controls = element('div', null, 'bb-review-controls'); controls.append(condition.wrapper, count.wrapper, all, add);
      view.body.append(controls, summary, results);
      let rows = [], loading = false, requestId = 0;
      function paint() {
        results.replaceChildren(); let total = 0, currency = '', quantity = 0;
        for (const row of rows.filter(row => row.id && !row.reason)) {
          const article = element('label', null, 'bb-review-row'), checkbox = element('input'); checkbox.type = 'checkbox'; checkbox.checked = selected.has(row.key); checkbox.disabled = !row.id || !!row.reason || loading;
          checkbox.onchange = () => { count.control.value = ''; if (checkbox.checked) selected.add(row.key); else selected.delete(row.key); paint(); };
          const info = element('span'); info.append(element('strong', row.card?.name || 'Unavailable card'), element('small', row.card ? row.card.setName + ' · ' + row.card.number + ' · ' + row.card.finish : row.reason));
          const detail = element('span', row.reason || row.condition + ' · ' + money(row.price, row.currency));
          article.append(checkbox, info, detail); results.append(article);
          if (checkbox.checked && !checkbox.disabled) { total += row.price; quantity++; currency = row.currency; }
        }
        if (!loading) summary.textContent = quantity ? quantity + ' selected · ' + money(total, currency || 'CAD') : 'No matching selections available to add.';
        add.textContent = 'Add ' + quantity + (quantity === 1 ? ' card' : ' cards') + ' to cart';
        add.disabled = loading || !quantity; count.control.max = String(rows.filter(row => row.id && !row.reason).length);
      }
      async function load() {
        const ticket = ++requestId; loading = true; selected.clear(); add.disabled = true;
        let failure = '';
        try {
          const keys = model.wanted(store.state, store.state.binders[currentBook]);
          const result = await shopping.review(keys, condition.control.value, (done, total) => { if (ticket === requestId && view.version === modalVersion) summary.textContent = 'Checking listings ' + done + ' / ' + total; });
          if (ticket !== requestId || view.version !== modalVersion) return;
          rows = result; rows.filter(row => row.id && !row.reason).forEach(row => selected.add(row.key));
        } catch (error) { failure = error.message; }
        finally { loading = false; if (view.version === modalVersion) { paint(); if (failure) summary.textContent = failure; } }
      }
      async function submit() {
        if (loading) return;
        if (count.control.value !== '') selectFirst();
        loading = true; add.disabled = true; condition.control.disabled = true; summary.textContent = 'Rechecking your selected copies…';
        try {
          const outcome = await shopping.add(rows.filter(row => selected.has(row.key) && !store.state.owned[row.key]), condition.control.value,
            (done, total) => { if (view.version === modalVersion) summary.textContent = 'Processing cards ' + done + ' / ' + total; });
          if (view.version !== modalVersion) return;
          if (outcome.needsReview) { rows = outcome.rows; selected.clear(); rows.filter(row => row.id && !row.reason).forEach(row => selected.add(row.key)); loading = false; paint(); summary.textContent = outcome.message; return; }
          const confirmed = new Set(outcome.added.map(row => row.key));
          rows = rows.map(row => confirmed.has(row.key) ? {...row, reason: 'Added to cart'} : row);
          selected.clear(); loading = false; paint();
          summary.textContent = outcome.message || outcome.added.length + ' added to cart' + (outcome.missing.length ? ' · ' + outcome.missing.length + ' unavailable or needing attention' : '');
          view.body.append(button('View cart', () => { if (window.KillinKiosk) { closeModal(); window.KillinKiosk.openCart(); } else location.assign((window.Shopify?.routes?.root || '/') + 'cart'); }));
          window.KillinBehavior?.record('cart', {action: 'builder_bulk_add', source: 'binder_builder', count: outcome.added.length});
        } catch (error) { summary.textContent = error.message; }
        finally { loading = false; condition.control.disabled = false; add.disabled = !selected.size; }
      }
      condition.control.onchange = load;
      function selectFirst() { selected.clear(); const number = Math.max(0, Math.floor(Number(count.control.value) || 0)); rows.filter(row => row.id && !row.reason).slice(0, number).forEach(row => selected.add(row.key)); paint(); }
      count.control.oninput = selectFirst;
      count.control.onchange = selectFirst;
      await load();
    }
    async function transferDialog() {
      const view = modal('Continue on your phone'), message = element('p', 'Preparing your binder transfer…'), image = element('div', null, 'bb-transfer-qr');
      view.body.append(message, image);
      const link = await shopping.transfer(book(), store.state.owned);
      if (view.version !== modalVersion) return;
      qr(image, link); message.textContent = 'Scan to import this draft, then sign in on your phone to save it. Available for 24 hours.';
      const copy = input('Transfer link', link); copy.control.readOnly = true;
      view.body.append(copy.wrapper, button('Copy link', async () => { try { await navigator.clipboard.writeText(link); message.textContent = 'Transfer link copied.'; } catch (_) { copy.control.focus(); copy.control.select(); } }));
    }
    async function receive() {
      const incoming = window.KillinBinderIncoming?.transfer; if (!incoming) return;
      delete window.KillinBinderIncoming.transfer;
      alert('Importing your kiosk draft…');
      try {
        const result = await shopping.receiveTransfer(incoming);
        let target = Object.keys(store.state.binders).find(key => store.state.binders[key].uid === result.binder.uid);
        edit(state => {
          target = target || Array.from({length: model.MAX_BINDERS}, (_, i) => 'b' + i).find(key => !state.binders[key]);
          if (!target) throw Error('You have 10 binders. Remove one, then scan the transfer again.');
          state.binders[target] = result.binder; Object.assign(state.owned, result.owned);
        });
        bookKey = target; render();
        if (!store.persisted) {
          alert('Draft opened, but this device could not save it. Keep the transfer link and allow browser storage before trying again.');
          return;
        }
        await result.clear();
        alert('Draft imported. Use Save to my account to open it on another device.');
      } catch (error) { showError(error); }
    }
    async function open() {
      if (active) return;
      active = true; panel.hidden = false; root.dataset.builderActive = 'true';
      for (const node of root.querySelectorAll('.bv-layout,.bv-footer,.bv-shopping-help,.bv-share-feedback,.bv-landing-copy,.bv-heading-actions')) { savedHidden.set(node, node.hidden); node.hidden = true; }
      root.dispatchEvent(new Event('killin:builder-visibility'));
      alert('Opening your binder builder…');
      try { [manifest] = await Promise.all([catalog.index(), store.ready]); if (!active) return; render(); await receive(); }
      catch (error) { showError(error); empty.hidden = false; empty.replaceChildren(button('Retry opening builder', () => { active = false; open(); })); }
    }
    function close() {
      active = false; panel.hidden = true; renderVersion++; closeModal();
      delete root.dataset.builderActive;
      for (const [node, hidden] of savedHidden) node.hidden = hidden;
      savedHidden.clear(); root.dispatchEvent(new Event('killin:builder-visibility'));
    }
    pickerHost.addEventListener('click', event => { if (event.target === pickerHost) closeModal(); });
    pickerHost.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.stopPropagation(); closeModal(); }
      if (event.key !== 'Tab') return;
      const nodes = [...pickerHost.querySelectorAll('button,input,select,a[href]')].filter(node => !node.disabled && !node.closest('[hidden]'));
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    document.addEventListener('killin:builder-state', () => { if (!active) return; if (lastState !== store.state) render(); else updateStatus(); });
    mobile.addEventListener('change', () => { if (active) render(); });
    const api = {open, close, get active() { return active; }, reset() { close(); bookKey = ''; page = 0; selectedSlot = ''; undoStack.length = 0; store.resetKiosk(); }};
    instances.set(root, api); return api;
  }
  window.KillinBinderBuilder = {init, get active() { return !!document.querySelector('[data-builder-active="true"]'); },
    reset() { document.querySelectorAll('[data-binder-view]').forEach(root => instances.get(root)?.reset()); }};
})();
